import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, shareReplay, catchError, first } from 'rxjs/operators';
import { MuscleGroup } from 'src/models/muscle-group.model';

@Injectable({
  providedIn: 'root',
})
export class MuscleGroupService {
  private muscleGroups$: Observable<MuscleGroup[]> | undefined;
  private muscleColorMap: Map<string, string> = new Map();

  public muscleGroupData = [
    {
      muscles: ['#Biceps', '#Triceps', '#Forearms'],
      group: '#Arms',
      color: '#A7BED3',
    },
    {
      muscles: ['#UpperChest', '#MiddleChest', '#LowerChest'],
      color: '#FAD2E1',
      group: '#Chest',
    },
    {
      color: '#E2CFC4',
      muscles: ['#FrontDelts', '#SideDelts', '#RearDelts', '#Trapezius'],
      group: '#Shoulders',
    },
    {
      group: '#Core',
      muscles: ['#Abs', '#Obliques'],
      color: '#F9D6C1',
    },
    {
      group: '#Back',
      color: '#BFD7B5',
      muscles: ['#UpperBack', '#LowerBack', '#Lats'],
    },
    {
      muscles: [
        '#Quads',
        '#Hamstrings',
        '#Calves',
        '#Glutes',
        '#HipFlexors',
        '#InnerThighs',
      ],
      color: '#C6E2E9',
      group: '#Legs',
    },
  ];

  constructor() {}

  getMuscleGroups(): Observable<MuscleGroup[]> {
    if (!this.muscleGroups$) {
      this.muscleGroups$ = of(this.muscleGroupData).pipe(shareReplay(1));
      this.muscleGroups$.subscribe((groups) => {
        this.muscleColorMap = groups.reduce((map, group) => {
          map.set(group.group, group.color);
          group.muscles.forEach((muscle) => map.set(muscle, group.color));
          return map;
        }, new Map<string, string>());
      });
    }

    return this.muscleGroups$;
  }

  getColorByMuscle(muscleName: string): Observable<string> {
    let color = this.muscleColorMap.get(muscleName);
    if (color) {
      return of(color);
    } else {
      return this.getMuscleGroups().pipe(
        map(() => {
          let fetchedColor = this.muscleColorMap.get(muscleName);
          if (fetchedColor) {
            return fetchedColor;
          } else {
            throw new Error(`Color not found for muscle: ${muscleName}`);
          }
        }),
        catchError((error) => {
          console.error(error);
          // Handle the error or provide a fallback color
          return of('defaultColor'); // Replace 'defaultColor' with an actual fallback color
        })
      );
    }
  }

  isGroup(groupName: string): Observable<boolean> {
    return this.getMuscleGroups().pipe(
      map((groups) => groups.some((group) => group.group === groupName)),
      first() // Take the first emission and complete, ensuring the subscription is closed
    );
  }

  muscleGroupMap: Record<string, string> = {
    biceps: '#Arms',
    triceps: '#Arms',
    forearms: '#Arms',
    chest: '#Chest',
    shoulders: '#Shoulders',
    traps: '#Shoulders',
    abdominals: '#Core',
    'middle back': '#Back',
    'lower back': '#Back',
    lats: '#Back',
    neck: '#Back',
    hamstrings: '#Legs',
    adductors: '#Legs',
    quadriceps: '#Legs',
    calves: '#Legs',
    glutes: '#Legs',
    abductors: '#Legs',
  };

  getDbExerciseMuscleGroup(muscle: string): string | undefined {
    return this.muscleGroupMap[muscle];
  }

  getDbExerciseArrayMuscleGroup(muscles: string[]): string[] {
    const uniqueGroups = new Set<string>();

    muscles.forEach((muscle) => {
      const group = this.muscleGroupMap[muscle];
      if (group) {
        uniqueGroups.add(group);
      }
    });

    return Array.from(uniqueGroups);
  }

  private muscleLabelMapping: { [key: string]: string } = {
    '#Biceps': 'biceps',
    '#Triceps': 'triceps',
    '#Forearms': 'forearms',
    '#UpperChest': 'chest',
    '#MiddleChest': 'chest',
    '#LowerChest': 'chest',
    '#FrontDelts': 'front-shoulders',
    '#SideDelts': 'front-shoulders',
    '#RearDelts': 'rear-shoulders',
    '#Trapezius': 'traps',
    '#Abs': 'abdominals',
    '#Obliques': 'obliques',
    '#UpperBack': 'traps-middle',
    '#LowerBack': 'lowerback',
    '#Lats': 'lats',
    '#Quads': 'quads',
    '#Hamstrings': 'hamstrings',
    '#Calves': 'calves',
    '#Glutes': 'glutes',
    '#HipFlexors': 'hipflexors', // Assuming you want to add this
    '#InnerThighs': 'innerthighs', // Assuming you want to add this
  };

  public mapMuscleGroups(aggregatedData: { muscle: string; value: number }[]) {
    const mappedData: { [key: string]: number } = {};

    aggregatedData.forEach((item) => {
      const label = this.muscleLabelMapping[item.muscle];
      if (label) {
        if (!mappedData[label]) {
          mappedData[label] = 0;
        }
        mappedData[label] += item.value;
      }
    });

    return Object.keys(mappedData).map((key) => ({
      muscle: key,
      value: mappedData[key],
    }));
  }
}
